import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, Lock } from 'lucide-react';
import { ProgressStepper, PAYMENT_STEPS } from '../components/ProgressStepper';
import { usePaymentParams, useUpdatePaymentParams } from './PaymentRouter';

type AuthStep = 'email' | 'otp';

export function AuthPage() {
  const navigate = useNavigate();
  const params = usePaymentParams();
  const updateParams = useUpdatePaymentParams();

  const [step, setStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState(params?.email || '');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Check if user already has valid token
  useEffect(() => {
    checkExistingAuth();
  }, []);

  // Resend countdown
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  async function checkExistingAuth() {
    const token = params?.at || localStorage.getItem('pouchpay_token');

    if (token) {
      try {
        // Validate token
        const response = await fetch('https://api.pouchfinance.xyz/shared-kyc/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          // Token is valid, skip auth
          navigate('/pay/review');
          return;
        }
      } catch (err) {
        // Token invalid, continue with auth
        console.error('Token validation failed:', err);
      }
    }
  }

  async function handleSendOtp() {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://api.pouchfinance.xyz/shared-kyc/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          countryCode: params?.countryCode || 'NG'
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send OTP');
      }

      await response.json();

      // Move to OTP step
      setStep('otp');
      setResendCountdown(60); // 60 seconds cooldown

      // Store email
      updateParams({ email: email.toLowerCase().trim() });

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp() {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://api.pouchfinance.xyz/shared-kyc/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          otp
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Invalid verification code');
      }

      const data = await response.json();

      // Store token
      localStorage.setItem('pouchpay_token', data.token);
      updateParams({ at: data.token });

      // Check if user has KYC
      // Navigate to KYC page or Review page
      navigate('/pay/review');

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleResendOtp() {
    if (resendCountdown > 0) return;

    await handleSendOtp();
  }

  function handleBack() {
    if (step === 'otp') {
      setStep('email');
      setOtp('');
      setError(null);
    } else {
      navigate('/pay/wallet');
    }
  }

  // Auto-submit when OTP is 6 digits
  useEffect(() => {
    if (otp.length === 6) {
      handleVerifyOtp();
    }
  }, [otp]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Stepper */}
      <ProgressStepper
        steps={PAYMENT_STEPS}
        currentStep="auth"
        completedSteps={['amount', 'wallet']}
      />

      <div className="max-w-2xl mx-auto px-4 pb-8">
        {/* Custom branding */}
        {params?.logoUrl && (
          <div className="text-center mb-6">
            <img
              src={params.logoUrl}
              alt="Logo"
              className="h-12 w-auto mx-auto"
            />
          </div>
        )}

        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
          {step === 'email' ? 'Enter Your Email' : 'Check Your Email'}
        </h1>
        <p className="text-center text-gray-600 mb-6">
          {step === 'email'
            ? "We'll send you a one-time code to verify your identity"
            : `Enter the 6-digit code we sent to ${email}`
          }
        </p>

        {/* Main card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {step === 'email' ? (
            <>
              {/* Email input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <Mail className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand outline-none"
                  />
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleBack}
                  className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSendOtp}
                  disabled={loading || !email}
                  className="flex-2 py-3 px-6 bg-brand text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
                  style={{ flex: 2 }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Get Verification Code'
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* OTP input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <Lock className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtp(value);
                      setError(null);
                    }}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand outline-none text-center text-2xl font-mono tracking-widest"
                  />
                </div>
              </div>

              {/* Resend OTP */}
              <div className="mb-6 text-center">
                {resendCountdown > 0 ? (
                  <p className="text-sm text-gray-500">
                    Resend code in {resendCountdown}s
                  </p>
                ) : (
                  <button
                    onClick={handleResendOtp}
                    className="text-sm text-brand hover:text-brand-600 font-medium"
                  >
                    Didn't receive code? Resend
                  </button>
                )}
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleBack}
                  className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6}
                  className="flex-2 py-3 px-6 bg-brand text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
                  style={{ flex: 2 }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a
              href="https://pouchfinance.xyz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:text-brand-600 font-medium"
            >
              PouchPay
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
